import { Manifest } from '../types';
import { getApiServer, appendAuthToken } from '../config';
import { TierAccessService, TileAccessDenial } from './TierAccessService';

export interface DecodedPointForecast {
  cycleEpoch: number;
  numHours: number;
  model?: string;
  rawHourOffsets?: number[];
  tempsC: number[];
  tempsF: number[];
  cloudCoverTotal: number[];
  cloudCoverLow: number[];
  cloudCoverMid: number[];
  cloudCoverHigh: number[];
  precipRateMmH: number[];
  precipProb: number[];
  snowRateCmH: number[];
  windSpeedMph: number[];
  windDirDeg: number[];
  windGustMph: number[];
  relativeHumidity: number[];
  dewpointC?: number[];
  dewpointF?: number[];
  dewRiskScore: number[];
  freezingLevelFt: number[];
  windchillF: number[];
  smokeSfc: number[];
  aqiValues: number[];
  aqhiValues?: number[];
  isAerosolOnly?: boolean;
  lightningHazard: number[];
  darkskiesScore: number[];
  sunIntensityWm2: number[];
  seeingScore?: number[];
  turbulenceEdr?: number[];
  // Format Version 4: Cloud Base & Cloud Top Elevation (ft MSL)
  cloudBaseFt?: number[];
  cloudTopFt?: number[];
  // Format Version 5: Golden Window Solar & Alpenglow Scores (0-100)
  sunriseScore?: number[];
  sunsetScore?: number[];
  alpenglowMorning?: number[];
  alpenglowEvening?: number[];
  goldenHourMorning?: number[];
  goldenHourEvening?: number[];
  // Format Version 3: Multi-Level Isobaric Atmospheric Soundings
  hasSounding?: boolean;
  soundingLevels?: number[];
  soundingTempsC?: Record<number, number[]>;
  soundingHeightsM?: Record<number, number[]>;
  soundingRh?: Record<number, number[]>;
  soundingWindSpeedMph?: Record<number, number[]>;
  soundingWindDirDeg?: Record<number, number[]>;
}

export interface DecodedValleyFog {
  cycleEpoch: number;
  numHours: number;
  model?: string;
  rawHourOffsets?: number[];
  fogProbability: number[];
  inversionDepthM: number[];
  surfaceRh: number[];
}

/**
 * Returns the forecast hour offset for each discrete step in a model's time series.
 * With uniform 1-hour spatial binary tiles, index i strictly corresponds to forecast hour i.
 */
export function getRawHourOffsetsForModel(_model: string, numSteps: number): number[] {
  const offsets: number[] = new Array(numSteps);
  for (let i = 0; i < numSteps; i++) {
    offsets[i] = i;
  }
  return offsets;
}

/**
 * Converts a 0-100 Astronomical Seeing Quality Score into full width at half maximum (FWHM)
 * resolution in arcseconds. 100 = 0.8" (pristine), 80 = 1.0", 60 = 1.5", 40 = 2.0", 20 = 2.5", 0 = 3.5" (turbulent).
 */
export function seeingScoreToArcsec(score: number): number {
  if (score >= 80) return Number((0.8 + (100 - score) / 20 * 0.2).toFixed(2));
  if (score >= 60) return Number((1.0 + (80 - score) / 20 * 0.5).toFixed(2));
  if (score >= 40) return Number((1.5 + (60 - score) / 20 * 0.5).toFixed(2));
  if (score >= 20) return Number((2.0 + (40 - score) / 20 * 0.5).toFixed(2));
  return Number((2.5 + (20 - Math.max(0, score)) / 20 * 1.0).toFixed(2));
}

export class ForecastBinaryService {
  public static lastAuthDenied = false;
  public static lastAccessDenial: TileAccessDenial | null = null;
  private static pointCache = new Map<string, DecodedPointForecast | null>();
  private static fogPointCache = new Map<string, DecodedValleyFog | null>();

  public static clearCache(): void {
    this.pointCache.clear();
    this.fogPointCache.clear();
    this.lastAuthDenied = false;
    this.lastAccessDenial = null;
  }

  /**
   * Computes accurate dew point temperature in Celsius and Fahrenheit using the Magnus-Tetens formula.
   * Guarantees that temperature and relative humidity come from the exact same meteorological source.
   */
  public static calculateDewPoint(tempC: number, rhPct: number): { dewpointC: number; dewpointF: number } {
    const rh = Math.max(1, Math.min(100, rhPct));
    const a = 17.27;
    const b = 237.7;
    const alpha = ((a * tempC) / (b + tempC)) + Math.log(rh / 100.0);
    const dpC = Math.round(((b * alpha) / (a - alpha)) * 10) / 10;
    const dpF = Math.round(dpC * 1.8 + 32);
    return { dewpointC: dpC, dewpointF: dpF };
  }

  private static lng2tile(lon: number, zoom: number): number {
    return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
  }

  private static lat2tile(lat: number, zoom: number): number {
    const latRad = (lat * Math.PI) / 180;
    return Math.floor(
      ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) *
        Math.pow(2, zoom)
    );
  }

  private static tileBounds(zoom: number, tx: number, ty: number) {
    const n = Math.pow(2, zoom);
    const lonMin = (tx / n) * 360.0 - 180.0;
    const lonMax = ((tx + 1) / n) * 360.0 - 180.0;
    const latRadMax = Math.atan(Math.sinh(Math.PI * (1.0 - (2.0 * ty) / n)));
    const latMax = (latRadMax * 180.0) / Math.PI;
    const latRadMin = Math.atan(Math.sinh(Math.PI * (1.0 - (2.0 * (ty + 1)) / n)));
    const latMin = (latRadMin * 180.0) / Math.PI;
    return { latMin, latMax, lonMin, lonMax };
  }

  public static getRawHourOffsetsForModel = getRawHourOffsetsForModel;

  /**
   * Maps a continuous forecast hour offset to the discrete step index in the binary tile vector.
   * With uniform 1-hour spatial binary tiles, index i maps directly to hour i.
   */
  public static getStepIndexForHour(hourOffset: number, numHours: number, _model: string = 'blend'): number {
    if (hourOffset <= 0 || numHours <= 0) return 0;
    return Math.max(0, Math.min(Math.round(hourOffset), numHours - 1));
  }

  public static normalizeModelKey(model: string): string {
    let clean = (model || 'blend').toLowerCase();
    if (clean.startsWith('gw_v2_')) {
      clean = clean.replace(/^gw_v2_/, '');
    }
    if (clean === 'blend_50' || clean === 'goldenhour_blend' || clean === 'alpenglow_blend') {
      return 'blend';
    }
    return clean;
  }

  public static async getForecastForPoint(
    lat: number,
    lon: number,
    manifest: Manifest,
    model: string = 'blend'
  ): Promise<DecodedPointForecast | null> {
    const zoom = manifest?.forecast_point_tiles?.zoom ?? 4;
    const tileX = this.lng2tile(lon, zoom);
    const tileY = this.lat2tile(lat, zoom);

    const { latMin, latMax, lonMin, lonMax } = this.tileBounds(zoom, tileX, tileY);
    if (lat < latMin || lat > latMax || lon < lonMin || lon > lonMax) {
      return null;
    }

    const gridW = manifest?.forecast_point_tiles?.grid_dim ?? 48;
    const gridH = manifest?.forecast_point_tiles?.grid_dim ?? 48;
    const px = Math.max(0, Math.min(gridW - 1, Math.floor(((lon - lonMin) / (lonMax - lonMin)) * gridW)));
    const py = Math.max(0, Math.min(gridH - 1, Math.floor(((latMax - lat) / (latMax - latMin)) * gridH)));
    const cellIdx = py * gridW + px;

    const cleanModel = this.normalizeModelKey(model);

    // Dynamic client-side downscaling for SKYSKOUT HRes 0.8km/0.5km Topo (derived from NOAA NBM)
    if (cleanModel === 'skysk_hres_topo' || cleanModel === 'nbm_highres') {
      const cacheKey = `skysk_hres_topo:${lat.toFixed(4)}:${lon.toFixed(4)}`;
      if (this.pointCache.has(cacheKey)) {
        return this.pointCache.get(cacheKey) || null;
      }

      const baseForecast = await this.getForecastForPoint(lat, lon, manifest, 'nbm');
      if (!baseForecast) {
        if (!this.lastAccessDenial) {
          this.pointCache.set(cacheKey, null);
        }
        return null;
      }

      try {
        const { getElevationAt, getBaseElevationAt } = await import('./PoiDataService');
        const profile = await getElevationAt(lat, lon);
        const baseElev = await getBaseElevationAt(lat, lon);
        const deltaZ_ft = profile.elevation - baseElev;

        // Standard lapse rate gamma ~ 6.5 C / 1000m (approx 1.98 C / 1000ft, 3.56 F / 1000ft)
        const lapseC = (deltaZ_ft / 3280.84) * 6.5;
        const lapseF = lapseC * 1.8;

        const tempsC = baseForecast.tempsC.map(c => Math.round((c - lapseC) * 10) / 10);
        const tempsF = baseForecast.tempsF.map(f => Math.round(f - lapseF));

        const dewpointC: number[] = [];
        const dewpointF: number[] = [];
        for (let h = 0; h < baseForecast.numHours; h++) {
          const t = tempsC[h];
          const rh = baseForecast.relativeHumidity[h] ? Math.max(1, Math.min(100, baseForecast.relativeHumidity[h])) : 50;
          const dp = ForecastBinaryService.calculateDewPoint(t, rh);
          dewpointC.push(dp.dewpointC);
          dewpointF.push(dp.dewpointF);
        }

        const downscaled: DecodedPointForecast = {
          ...baseForecast,
          model: 'skysk_hres_topo',
          tempsC,
          tempsF,
          dewpointC,
          dewpointF
        };

        this.pointCache.set(cacheKey, downscaled);
        return downscaled;
      } catch (err) {
        console.warn('[ForecastBinaryService] Failed to downscale skysk_hres_topo:', err);
        return baseForecast;
      }
    }

    const modelMeta = (manifest?.forecast_point_tiles as any)?.models_meta?.[cleanModel];
    const defaultHoursByModel: Record<string, number> = {
      hrrr: 48,
      rrfs: 85,
      rdps: 85,
      icon13: 109,
      ecmwf: 129,
      gdps: 129,
      gfs: 129,
      raqdps: 73,
      gefs: 89,
      gefs_dust: 89,
      gw_nowcast: 36,
      gw_nowcast_2h: 36,
      gw_nowcast_4h: 36,
      gw_nowcast_6h: 36
    };
    const modelHours = modelMeta?.hours ?? defaultHoursByModel[cleanModel] ?? (manifest?.forecast_point_tiles?.hours ?? 240);

    const apiServer = getApiServer(manifest);
    const tileBase = (manifest ? manifest.tile_base : 'tiles').replace(/^\/+|\/+$/g, '');
    const tileSubdir = manifest?.forecast_point_tiles?.tile_path || 'forecast/forecast_z4';
    const cleanServer = apiServer.replace(/\/$/, '');

    const hasModelSubdir = (manifest?.forecast_point_tiles as any)?.available_models?.includes(cleanModel);
    const defaultNowcastTilePaths: Record<string, string> = {
      gw_nowcast: 'observed/goldenwindow/point_tiles',
      gw_nowcast_2h: 'observed/goldenwindow/point_tiles_2h',
      gw_nowcast_4h: 'observed/goldenwindow/point_tiles_4h',
      gw_nowcast_6h: 'observed/goldenwindow/point_tiles_6h'
    };
    let subPath = cleanModel in defaultNowcastTilePaths
      ? (modelMeta?.tile_path || defaultNowcastTilePaths[cleanModel])
      : (cleanModel !== 'blend' || hasModelSubdir ? `${tileSubdir}/${cleanModel}` : `${tileSubdir}/${cleanModel}`);
    let cleanPath = `${tileBase}/${subPath}/${tileX}/${tileY}.bin`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
    let tileUrl = `${cleanServer}/${cleanPath}`;

    const cacheKey = `${tileUrl}:${cleanModel}:${cellIdx}`;
    if (this.pointCache.has(cacheKey)) {
      return this.pointCache.get(cacheKey) || null;
    }

    const headerBytes = manifest?.forecast_point_tiles?.header_bytes ?? 48;
    const version = modelMeta?.version ?? (manifest?.forecast_point_tiles?.format_version ?? 5);
    const isV6 = version >= 6;
    const isV5 = version >= 5;
    const isV4 = version >= 4;
    const isV3 = version >= 3;
    const isAerosolOnly =
      modelMeta?.num_vars === 3 ||
      modelMeta?.bytes_per_hour === 5 ||
      ['gefs', 'gefs_dust', 'raqdps'].includes(cleanModel);
    const isSolarOnly =
      modelMeta?.num_vars === 6 ||
      modelMeta?.bytes_per_hour === 6 ||
      cleanModel.startsWith('gw_nowcast');
    const bytesPerHour = modelMeta?.bytes_per_hour ??
      (isAerosolOnly ? 5 : (isSolarOnly ? 6 : (isV6 ? 80 : (isV5 ? 79 : (isV4 ? 73 : (isV3 ? 69 : 29))))));
    const expectedCellBytes = 8 + modelHours * bytesPerHour;
    const cellBytes = (modelMeta?.cell_bytes && Math.abs(modelMeta.cell_bytes - expectedCellBytes) <= 4)
      ? modelMeta.cell_bytes
      : expectedCellBytes;
    const startByte = headerBytes + cellIdx * cellBytes;
    const endByte = startByte + cellBytes - 1;

    try {
      let authenticatedUrl = appendAuthToken(tileUrl);
      let resp = await fetch(authenticatedUrl, {
        credentials: 'include',
        headers: {
          Range: `bytes=${startByte}-${endByte}`
        }
      });

      if (!resp.ok && resp.status !== 206) {
        const denial = TierAccessService.parseTileAccessDenial(resp, 'pro');
        if (denial) {
          this.lastAuthDenied = true;
          this.lastAccessDenial = denial;
          // Do not poison pointCache with null on auth/subscription denial so charts refresh after login/upgrade
          return null;
        }
        this.pointCache.set(cacheKey, null);
        return null;
      }
      this.lastAuthDenied = false;
      this.lastAccessDenial = null;

      let rawBuffer = await resp.arrayBuffer();

      // If the server responded with 200 instead of 206 Partial Content, slice the expected range
      let cellBuffer: ArrayBuffer;
      if (resp.status === 206 || rawBuffer.byteLength === cellBytes) {
        cellBuffer = rawBuffer;
      } else if (rawBuffer.byteLength >= endByte + 1) {
        cellBuffer = rawBuffer.slice(startByte, endByte + 1);
      } else if (rawBuffer.byteLength >= 2) {
        // Fallback for legacy compressed files if encountered
        const headerBytesArr = new Uint8Array(rawBuffer, 0, 2);
        if (headerBytesArr[0] === 0x1f && headerBytesArr[1] === 0x8b && typeof DecompressionStream !== 'undefined') {
          const stream = new Response(rawBuffer).body?.pipeThrough(new DecompressionStream('gzip'));
          if (stream) {
            const decomp = await new Response(stream).arrayBuffer();
            if (decomp.byteLength >= endByte + 1) {
              cellBuffer = decomp.slice(startByte, endByte + 1);
            } else {
              this.pointCache.set(cacheKey, null);
              return null;
            }
          } else {
            this.pointCache.set(cacheKey, null);
            return null;
          }
        } else {
          this.pointCache.set(cacheKey, null);
          return null;
        }
      } else {
        this.pointCache.set(cacheKey, null);
        return null;
      }

      if (cellBuffer.byteLength < 8) {
        this.pointCache.set(cacheKey, null);
        return null;
      }

      const view = new DataView(cellBuffer);
      const cycleEpoch = view.getUint32(0, true);
      const numSteps = view.getUint16(4, true);
      const minBytesPerStep = isAerosolOnly ? 5 : (isSolarOnly ? 6 : 29);

      if (numSteps === 0 || cellBuffer.byteLength < 8 + numSteps * minBytesPerStep) {
        this.pointCache.set(cacheKey, null);
        return null;
      }

      // Hybrid stepping awareness: 0-72h @ 1h, 75+h @ 3h intervals for global models
      const isHybrid = numSteps < 200 && (
        modelMeta?.step_transition_hour != null ||
        numSteps === 129 ||
        numSteps === 109 ||
        numSteps === 89 ||
        ['ecmwf', 'gfs', 'icon13', 'gdps', 'gefs', 'gefs_dust'].includes(cleanModel)
      );
      const transitionHour = modelMeta?.step_transition_hour ?? 72;
      const stepBeyond = modelMeta?.step_beyond ?? 3;
      const calculatedMaxFhr = isHybrid && numSteps > (transitionHour + 1)
        ? transitionHour + (numSteps - 1 - transitionHour) * stepBeyond
        : (isHybrid ? numSteps - 1 : numSteps);
      const effectiveMaxFhr = (modelMeta?.max_forecast_hour != null && modelMeta.max_forecast_hour >= calculatedMaxFhr)
        ? modelMeta.max_forecast_hour
        : calculatedMaxFhr;
      const outHours = isHybrid ? (effectiveMaxFhr + 1) : numSteps;

      const getScalar = (arr: ArrayLike<number>, hIdx: number): number => {
        if (!isHybrid || hIdx <= transitionHour) return arr[Math.min(hIdx, numSteps - 1)];
        const delta = hIdx - transitionHour;
        const k = Math.floor(delta / stepBeyond);
        const rem = delta % stepBeyond;
        const idx0 = transitionHour + k;
        if (rem === 0 || idx0 >= numSteps - 1) return arr[Math.min(idx0, numSteps - 1)];
        const idx1 = Math.min(numSteps - 1, idx0 + 1);
        const alpha = rem / stepBeyond;
        return (1 - alpha) * arr[idx0] + alpha * arr[idx1];
      };

      // Compact 3-variable aerosol cell layout (num_vars = 3, 5 bytes/step: smoke_sfc uint16, aqi uint16, aqhi uint8)
      if (cellBuffer.byteLength === 8 + numSteps * 5 || (isAerosolOnly && cellBuffer.byteLength < 8 + numSteps * 29)) {
        let aOff = 8;
        const smokeSfcArr = this.readUint16Array(view, aOff, numSteps);
        aOff += numSteps * 2;
        const aqiArr = this.readUint16Array(view, aOff, numSteps);
        aOff += numSteps * 2;
        const aqhiArr = this.readUint8Array(view, aOff, numSteps);

        const smokeSfc: number[] = new Array(outHours);
        const aqiValues: number[] = new Array(outHours);
        const aqhiValues: number[] = new Array(outHours);
        for (let h = 0; h < outHours; h++) {
          smokeSfc[h] = Math.round((getScalar(smokeSfcArr, h) / 10.0) * 10) / 10;
          aqiValues[h] = Math.round(getScalar(aqiArr, h));
          aqhiValues[h] = Math.round((getScalar(aqhiArr, h) / 10.0) * 10) / 10;
        }
        const zeros = () => new Array(outHours).fill(0);
        const decodedAerosol: DecodedPointForecast = {
          cycleEpoch,
          numHours: outHours,
          model,
          rawHourOffsets: getRawHourOffsetsForModel(model, outHours),
          isAerosolOnly: true,
          tempsC: zeros(),
          tempsF: new Array(outHours).fill(32),
          cloudCoverTotal: zeros(),
          cloudCoverLow: zeros(),
          cloudCoverMid: zeros(),
          cloudCoverHigh: zeros(),
          precipRateMmH: zeros(),
          precipProb: zeros(),
          snowRateCmH: zeros(),
          windSpeedMph: zeros(),
          windDirDeg: zeros(),
          windGustMph: zeros(),
          relativeHumidity: zeros(),
          dewpointC: zeros(),
          dewpointF: new Array(outHours).fill(32),
          dewRiskScore: zeros(),
          freezingLevelFt: zeros(),
          windchillF: new Array(outHours).fill(32),
          smokeSfc,
          aqiValues,
          aqhiValues,
          lightningHazard: zeros(),
          darkskiesScore: zeros(),
          sunIntensityWm2: zeros(),
          seeingScore: zeros(),
          cloudBaseFt: zeros(),
          cloudTopFt: zeros(),
          sunriseScore: zeros(),
          sunsetScore: zeros(),
          alpenglowMorning: zeros(),
          alpenglowEvening: zeros(),
          goldenHourMorning: zeros(),
          goldenHourEvening: zeros(),
          hasSounding: false
        };
        this.pointCache.set(cacheKey, decodedAerosol);
        return decodedAerosol;
      }

      // Compact 6-variable solar NowCast cell layout (num_vars = 6, 6 bytes/step: sunrise, sunset, alpenglow_morn, alpenglow_eve, goldenhour_morn, goldenhour_eve)
      if (cellBuffer.byteLength === 8 + numSteps * 6 || (isSolarOnly && cellBuffer.byteLength < 8 + numSteps * 29)) {
        let sOff = 8;
        const snRaw = new Uint8Array(cellBuffer, sOff, numSteps);
        sOff += numSteps;
        const ssRaw = new Uint8Array(cellBuffer, sOff, numSteps);
        sOff += numSteps;
        const amRaw = new Uint8Array(cellBuffer, sOff, numSteps);
        sOff += numSteps;
        const aeRaw = new Uint8Array(cellBuffer, sOff, numSteps);
        sOff += numSteps;
        const gmRaw = new Uint8Array(cellBuffer, sOff, numSteps);
        sOff += numSteps;
        const geRaw = new Uint8Array(cellBuffer, sOff, numSteps);

        const sunriseScore: number[] = new Array(outHours);
        const sunsetScore: number[] = new Array(outHours);
        const alpenglowMorning: number[] = new Array(outHours);
        const alpenglowEvening: number[] = new Array(outHours);
        const goldenHourMorning: number[] = new Array(outHours);
        const goldenHourEvening: number[] = new Array(outHours);
        for (let h = 0; h < outHours; h++) {
          sunriseScore[h] = Math.round(getScalar(snRaw, h));
          sunsetScore[h] = Math.round(getScalar(ssRaw, h));
          alpenglowMorning[h] = Math.round(getScalar(amRaw, h));
          alpenglowEvening[h] = Math.round(getScalar(aeRaw, h));
          goldenHourMorning[h] = Math.round(getScalar(gmRaw, h));
          goldenHourEvening[h] = Math.round(getScalar(geRaw, h));
        }
        const zeros = () => new Array(outHours).fill(0);
        const decodedSolar: DecodedPointForecast = {
          cycleEpoch,
          numHours: outHours,
          model,
          rawHourOffsets: getRawHourOffsetsForModel(model, outHours),
          tempsC: zeros(),
          tempsF: new Array(outHours).fill(32),
          cloudCoverTotal: zeros(),
          cloudCoverLow: zeros(),
          cloudCoverMid: zeros(),
          cloudCoverHigh: zeros(),
          precipRateMmH: zeros(),
          precipProb: zeros(),
          snowRateCmH: zeros(),
          windSpeedMph: zeros(),
          windDirDeg: zeros(),
          windGustMph: zeros(),
          relativeHumidity: zeros(),
          dewpointC: zeros(),
          dewpointF: new Array(outHours).fill(32),
          dewRiskScore: zeros(),
          freezingLevelFt: zeros(),
          windchillF: new Array(outHours).fill(32),
          smokeSfc: zeros(),
          aqiValues: zeros(),
          aqhiValues: zeros(),
          lightningHazard: zeros(),
          darkskiesScore: zeros(),
          sunIntensityWm2: zeros(),
          seeingScore: zeros(),
          cloudBaseFt: zeros(),
          cloudTopFt: zeros(),
          sunriseScore,
          sunsetScore,
          alpenglowMorning,
          alpenglowEvening,
          goldenHourMorning,
          goldenHourEvening,
          hasSounding: false
        };
        this.pointCache.set(cacheKey, decodedSolar);
        return decodedSolar;
      }

      const getDir = (arr: ArrayLike<number>, hIdx: number): number => {
        if (!isHybrid || hIdx <= transitionHour) return arr[Math.min(hIdx, numSteps - 1)];
        const delta = hIdx - transitionHour;
        const k = Math.floor(delta / stepBeyond);
        const rem = delta % stepBeyond;
        const idx0 = transitionHour + k;
        if (rem === 0 || idx0 >= numSteps - 1) return arr[Math.min(idx0, numSteps - 1)];
        const idx1 = Math.min(numSteps - 1, idx0 + 1);
        const alpha = rem / stepBeyond;
        const diff = ((arr[idx1] - arr[idx0] + 540) % 360) - 180;
        return (arr[idx0] + alpha * diff + 360) % 360;
      };

      let byteOffset = 8;

      // 0: temp (int16, tenths of degC) -> 2 bytes per step
      const tempArr = this.readInt16Array(view, byteOffset, numSteps);
      byteOffset += numSteps * 2;

      // 1-4: clouds (uint8, 0-100%) -> 1 byte per step each
      const cloudTotalArr = new Uint8Array(cellBuffer, byteOffset, numSteps);
      byteOffset += numSteps * 1;
      const cloudLowArr = new Uint8Array(cellBuffer, byteOffset, numSteps);
      byteOffset += numSteps * 1;
      const cloudMidArr = new Uint8Array(cellBuffer, byteOffset, numSteps);
      byteOffset += numSteps * 1;
      const cloudHighArr = new Uint8Array(cellBuffer, byteOffset, numSteps);
      byteOffset += numSteps * 1;

      // 5-7: precip & snow
      const precipRateArr = this.readUint16Array(view, byteOffset, numSteps); // 2 bytes
      byteOffset += numSteps * 2;
      const precipProbArr = new Uint8Array(cellBuffer, byteOffset, numSteps);  // 1 byte
      byteOffset += numSteps * 1;
      const snowRateArr = this.readUint16Array(view, byteOffset, numSteps);   // 2 bytes
      byteOffset += numSteps * 2;

      // 8-10: winds
      const windSpeedArr = new Uint8Array(cellBuffer, byteOffset, numSteps);   // 1 byte
      byteOffset += numSteps * 1;
      const windDirArr = this.readUint16Array(view, byteOffset, numSteps);    // 2 bytes
      byteOffset += numSteps * 2;
      const windGustArr = new Uint8Array(cellBuffer, byteOffset, numSteps);    // 1 byte
      byteOffset += numSteps * 1;

      // 11: relative humidity (uint8, 0-100%)
      const rhArr = new Uint8Array(cellBuffer, byteOffset, numSteps);          // 1 byte
      byteOffset += numSteps * 1;

      // 12: optics dew risk (uint8, 0-100)
      const dewRiskArr = new Uint8Array(cellBuffer, byteOffset, numSteps);     // 1 byte
      byteOffset += numSteps * 1;

      // 13: freezing level (uint16, tens of feet)
      const freezingLevelArr = this.readUint16Array(view, byteOffset, numSteps); // 2 bytes
      byteOffset += numSteps * 2;

      // 14: windchill (int16, tenths of degF)
      const windchillArr = this.readInt16Array(view, byteOffset, numSteps);   // 2 bytes
      byteOffset += numSteps * 2;

      // 15-16: smoke & aqi
      const smokeSfcArr = this.readUint16Array(view, byteOffset, numSteps);   // 2 bytes
      byteOffset += numSteps * 2;
      const aqiArr = this.readUint16Array(view, byteOffset, numSteps);        // 2 bytes
      byteOffset += numSteps * 2;

      // 17: lightning (uint8, 0-100)
      const lightningArr = new Uint8Array(cellBuffer, byteOffset, numSteps);   // 1 byte
      byteOffset += numSteps * 1;

      // 18: darkskies (uint8, 0-100)
      const darkskiesArr = new Uint8Array(cellBuffer, byteOffset, numSteps);   // 1 byte
      byteOffset += numSteps * 1;

      // 19: sun intensity (uint16, W/m2)
      const sunIntensityArr = this.readUint16Array(view, byteOffset, numSteps); // 2 bytes
      byteOffset += numSteps * 2;

      // 20 & 21: Format Version 4: cloud_base & cloud_top (uint16, ft MSL / 10)
      let cloudBaseFt: number[] | undefined;
      let cloudTopFt: number[] | undefined;
      if (isV4 && cellBuffer.byteLength >= byteOffset + numSteps * 4) {
        const cbRaw = this.readUint16Array(view, byteOffset, numSteps);
        byteOffset += numSteps * 2;
        const ctRaw = this.readUint16Array(view, byteOffset, numSteps);
        byteOffset += numSteps * 2;
        cloudBaseFt = [];
        cloudTopFt = [];
        for (let h = 0; h < outHours; h++) {
          cloudBaseFt.push(Math.round(getScalar(cbRaw, h) * 10));
          cloudTopFt.push(Math.round(getScalar(ctRaw, h) * 10));
        }
      }

      // 22-27: Format Version 5: Golden Window Solar & Alpenglow Scores (uint8, 0-100)
      let sunriseScore: number[] | undefined;
      let sunsetScore: number[] | undefined;
      let alpenglowMorning: number[] | undefined;
      let alpenglowEvening: number[] | undefined;
      let goldenHourMorning: number[] | undefined;
      let goldenHourEvening: number[] | undefined;
      if (isV5 && cellBuffer.byteLength >= byteOffset + numSteps * 6) {
        const snRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
        byteOffset += numSteps * 1;
        const ssRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
        byteOffset += numSteps * 1;
        const amRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
        byteOffset += numSteps * 1;
        const aeRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
        byteOffset += numSteps * 1;
        const gmRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
        byteOffset += numSteps * 1;
        const geRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
        byteOffset += numSteps * 1;

        sunriseScore = [];
        sunsetScore = [];
        alpenglowMorning = [];
        alpenglowEvening = [];
        goldenHourMorning = [];
        goldenHourEvening = [];
        for (let h = 0; h < outHours; h++) {
          sunriseScore.push(Math.round(getScalar(snRaw, h)));
          sunsetScore.push(Math.round(getScalar(ssRaw, h)));
          alpenglowMorning.push(Math.round(getScalar(amRaw, h)));
          alpenglowEvening.push(Math.round(getScalar(aeRaw, h)));
          goldenHourMorning.push(Math.round(getScalar(gmRaw, h)));
          goldenHourEvening.push(Math.round(getScalar(geRaw, h)));
        }
      }

      // 28: Format Version 6: Astronomical Seeing Score (uint8, 0-100)
      let seeingArr: Uint8Array | undefined;
      if (isV6 && cellBuffer.byteLength >= byteOffset + numSteps * 1) {
        seeingArr = new Uint8Array(cellBuffer, byteOffset, numSteps);
        byteOffset += numSteps * 1;
      }

      const tempsC: number[] = [];
      const tempsF: number[] = [];
      const cloudCoverTotal: number[] = [];
      const cloudCoverLow: number[] = [];
      const cloudCoverMid: number[] = [];
      const cloudCoverHigh: number[] = [];
      const precipRateMmH: number[] = [];
      const precipProb: number[] = [];
      const snowRateCmH: number[] = [];
      const windSpeedMph: number[] = [];
      const windDirDeg: number[] = [];
      const windGustMph: number[] = [];
      const relativeHumidity: number[] = [];
      const dewpointC: number[] = [];
      const dewpointF: number[] = [];
      const dewRiskScore: number[] = [];
      const freezingLevelFt: number[] = [];
      const windchillF: number[] = [];
      const smokeSfc: number[] = [];
      const aqiValues: number[] = [];
      const lightningHazard: number[] = [];
      const darkskiesScore: number[] = [];
      const sunIntensityWm2: number[] = [];
      const seeingScore: number[] = [];

      // Pre-pass: copy tempArr into cleanTemps and repair any 0.0°C unpopulated slice anomalies
      const cleanTempsC: number[] = new Array(numSteps);
      for (let s = 0; s < numSteps; s++) {
        cleanTempsC[s] = tempArr[s] / 10.0;
      }
      let scanIdx = 0;
      while (scanIdx < numSteps) {
        if (tempArr[scanIdx] === 0) {
          let endRun = scanIdx;
          while (endRun < numSteps && tempArr[endRun] === 0) {
            endRun++;
          }
          const runLen = endRun - scanIdx;
          const prevVal = scanIdx > 0 ? cleanTempsC[scanIdx - 1] : (endRun < numSteps ? cleanTempsC[endRun] : 0);
          const nextVal = endRun < numSteps ? cleanTempsC[endRun] : prevVal;
          // If run is short (1 to 4 hours) and surrounding temperatures are non-zero with significant difference (> 3.5°C)
          if (runLen <= 4 && (Math.abs(prevVal) > 3.5 || Math.abs(nextVal) > 3.5)) {
            for (let k = scanIdx; k < endRun; k++) {
              const frac = (k - scanIdx + 1) / (runLen + 1);
              cleanTempsC[k] = prevVal + frac * (nextVal - prevVal);
            }
          }
          scanIdx = endRun;
        } else {
          scanIdx++;
        }
      }

      for (let h = 0; h < outHours; h++) {
        const cVal = getScalar(cleanTempsC, h);
        tempsC.push(Math.round(cVal * 10) / 10);
        tempsF.push(Math.round((cVal * 1.8 + 32) * 10) / 10);

        cloudCoverTotal.push(Math.round(getScalar(cloudTotalArr, h)));
        cloudCoverLow.push(Math.round(getScalar(cloudLowArr, h)));
        cloudCoverMid.push(Math.round(getScalar(cloudMidArr, h)));
        cloudCoverHigh.push(Math.round(getScalar(cloudHighArr, h)));

        precipRateMmH.push(Math.round(getScalar(precipRateArr, h) / 100.0 * 100) / 100);
        precipProb.push(Math.round(getScalar(precipProbArr, h)));
        snowRateCmH.push(Math.round(getScalar(snowRateArr, h) / 10.0 * 10) / 10);

        windSpeedMph.push(Math.round(getScalar(windSpeedArr, h)));
        windDirDeg.push(Math.round(getDir(windDirArr, h)));
        windGustMph.push(Math.round(getScalar(windGustArr, h)));

        const rhVal = Math.max(1, Math.min(100, Math.round(getScalar(rhArr, h))));
        relativeHumidity.push(rhVal);

        // Client-side derived Dew Point strictly calculated from the same model's temperature and RH
        const dp = ForecastBinaryService.calculateDewPoint(cVal, rhVal);
        dewpointC.push(dp.dewpointC);
        dewpointF.push(dp.dewpointF);

        dewRiskScore.push(Math.round(getScalar(dewRiskArr, h)));
        freezingLevelFt.push(Math.round(getScalar(freezingLevelArr, h) * 10));
        windchillF.push(Math.round(getScalar(windchillArr, h) / 10.0));

        smokeSfc.push(Math.round(getScalar(smokeSfcArr, h) / 10.0 * 10) / 10);
        aqiValues.push(Math.round(getScalar(aqiArr, h)));
        lightningHazard.push(Math.round(getScalar(lightningArr, h)));
        darkskiesScore.push(Math.round(getScalar(darkskiesArr, h)));
        sunIntensityWm2.push(Math.round(getScalar(sunIntensityArr, h)));
        if (seeingArr) {
          seeingScore.push(Math.round(getScalar(seeingArr, h)));
        }
      }

      let hasSounding = false;
      const soundingLevels = [925, 850, 700, 500, 300];
      const soundingTempsC: Record<number, number[]> = {};
      const soundingHeightsM: Record<number, number[]> = {};
      const soundingRh: Record<number, number[]> = {};
      const soundingWindSpeedMph: Record<number, number[]> = {};
      const soundingWindDirDeg: Record<number, number[]> = {};

      if (cellBuffer.byteLength >= byteOffset + numSteps * 40) {
        hasSounding = true;
        for (const lev of soundingLevels) {
          const tRaw = this.readInt16Array(view, byteOffset, numSteps);
          byteOffset += numSteps * 2;
          const zRaw = this.readUint16Array(view, byteOffset, numSteps);
          byteOffset += numSteps * 2;
          const rhRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
          byteOffset += numSteps * 1;
          const spdRaw = new Uint8Array(cellBuffer, byteOffset, numSteps);
          byteOffset += numSteps * 1;
          const dirRaw = this.readUint16Array(view, byteOffset, numSteps);
          byteOffset += numSteps * 2;

          soundingTempsC[lev] = [];
          soundingHeightsM[lev] = [];
          soundingRh[lev] = [];
          soundingWindSpeedMph[lev] = [];
          soundingWindDirDeg[lev] = [];

          for (let h = 0; h < outHours; h++) {
            soundingTempsC[lev].push(Math.round((getScalar(tRaw, h) / 10.0) * 10) / 10);
            soundingHeightsM[lev].push(Math.round(getScalar(zRaw, h) * 10)); // decameters to meters
            soundingRh[lev].push(Math.round(getScalar(rhRaw, h)));
            soundingWindSpeedMph[lev].push(Math.round(getScalar(spdRaw, h)));
            soundingWindDirDeg[lev].push(Math.round(getDir(dirRaw, h)));
          }
        }
      }

      // If seeing was not present in the tile (e.g. format v5), compute physics-based turbulence fallback
      if (!seeingArr) {
        for (let h = 0; h < outHours; h++) {
          if (hasSounding && soundingWindSpeedMph[300] && soundingWindSpeedMph[300][h] !== undefined) {
            const jetSpd = soundingWindSpeedMph[300][h] * 0.44704;
            const jetPenalty = Math.min(35, Math.max(0, (jetSpd - 15) / 40 * 35));
            const sfcWind = windSpeedMph[h] || 0;
            const windPenalty = Math.min(25, Math.max(0, (sfcWind - 8) * 1.5));
            const estScore = Math.max(10, Math.min(95, Math.round(95 - jetPenalty - windPenalty)));
            seeingScore.push(estScore);
          } else {
            seeingScore.push(65);
          }
        }
      }

      // Compute upper-tropospheric Clear-Air Turbulence (Ellrod-Richardson EDR diagnostic, 0.00 - 1.00)
      // directly from real decoded multi-level isobaric sounding wind vectors, heights, and temperatures.
      let turbulenceEdr: number[] | undefined = undefined;
      if (hasSounding && soundingWindSpeedMph[300] && soundingWindSpeedMph[500]) {
        const rawEdr: number[] = new Array(outHours);
        const toUV = (spdMph: number, dirDeg: number): [number, number] => {
          const spdMs = (spdMph || 0) * 0.44704;
          const rad = ((dirDeg || 0) * Math.PI) / 180.0;
          return [-spdMs * Math.sin(rad), -spdMs * Math.cos(rad)];
        };

        for (let h = 0; h < outHours; h++) {
          const [u300, v300] = toUV(soundingWindSpeedMph[300][h], soundingWindDirDeg[300]?.[h] ?? 0);
          const [u500, v500] = toUV(soundingWindSpeedMph[500][h], soundingWindDirDeg[500]?.[h] ?? 0);
          const z300 = soundingHeightsM[300]?.[h] || 9164;
          const z500 = soundingHeightsM[500]?.[h] || 5574;
          const dzUpper = Math.max(1000, Math.abs(z300 - z500));
          const dvUpper = Math.hypot(u300 - u500, v300 - v500);
          let vwsMax = dvUpper / dzUpper;

          if (soundingWindSpeedMph[700]) {
            const [u700, v700] = toUV(soundingWindSpeedMph[700][h], soundingWindDirDeg[700]?.[h] ?? 0);
            const z700 = soundingHeightsM[700]?.[h] || 3012;
            const dzMid = Math.max(800, Math.abs(z500 - z700));
            const dvMid = Math.hypot(u500 - u700, v500 - v700);
            vwsMax = Math.max(vwsMax, 0.85 * (dvMid / dzMid));
          }

          // Potential temperature static stability modulation (Brunt-Vaisala N^2)
          const t300K = (soundingTempsC[300]?.[h] ?? -45.0) + 273.15;
          const t500K = (soundingTempsC[500]?.[h] ?? -20.0) + 273.15;
          const theta300 = t300K * Math.pow(1000.0 / 300.0, 0.2854);
          const theta500 = t500K * Math.pow(1000.0 / 500.0, 0.2854);
          const thetaMean = 0.5 * (theta300 + theta500);
          const nSq = Math.max(3e-5, (9.80665 / Math.max(200, thetaMean)) * ((theta300 - theta500) / dzUpper));
          const stabilityFactor = Math.min(1.45, Math.max(0.75, Math.sqrt(1.5e-4 / nSq)));

          // Characteristic jet-streak horizontal deformation scale + temporal wind vector shift
          const jetSpd300 = Math.hypot(u300, v300);
          let temporalDef = 0;
          if (h > 0 && h < outHours - 1) {
            const [uPrev, vPrev] = toUV(soundingWindSpeedMph[300][h - 1], soundingWindDirDeg[300]?.[h - 1] ?? 0);
            const [uNext, vNext] = toUV(soundingWindSpeedMph[300][h + 1], soundingWindDirDeg[300]?.[h + 1] ?? 0);
            const advSpeed = Math.max(15.0, jetSpd300 * 0.65);
            temporalDef = Math.hypot(uNext - uPrev, vNext - vPrev) / (2.0 * 3600.0 * advSpeed);
          }
          const defEst = Math.max(1.8e-5, jetSpd300 / 4.5e5 + 0.5 * temporalDef);

          // Ellrod TI1 = VWS * DEF calibrated to ICAO EDR (0.00 - 1.00) matching turbulence_processor.py
          const ti1 = vwsMax * defEst * stabilityFactor;
          rawEdr[h] = Math.min(1.0, Math.max(0.0, Math.sqrt(Math.max(0.0, ti1 * 1.5e5))));
        }

        turbulenceEdr = new Array(outHours);
        for (let h = 0; h < outHours; h++) {
          const prev = rawEdr[Math.max(0, h - 1)];
          const curr = rawEdr[h];
          const next = rawEdr[Math.min(outHours - 1, h + 1)];
          turbulenceEdr[h] = Math.round((0.25 * prev + 0.5 * curr + 0.25 * next) * 100) / 100;
        }
      }

      const decoded: DecodedPointForecast = {
        cycleEpoch,
        numHours: outHours,
        model,
        rawHourOffsets: getRawHourOffsetsForModel(model, outHours),
        tempsC,
        tempsF,
        cloudCoverTotal,
        cloudCoverLow,
        cloudCoverMid,
        cloudCoverHigh,
        precipRateMmH,
        precipProb,
        snowRateCmH,
        windSpeedMph,
        windDirDeg,
        windGustMph,
        relativeHumidity,
        dewpointC,
        dewpointF,
        dewRiskScore,
        freezingLevelFt,
        windchillF,
        smokeSfc,
        aqiValues,
        lightningHazard,
        darkskiesScore,
        sunIntensityWm2,
        seeingScore,
        turbulenceEdr,
        cloudBaseFt,
        cloudTopFt,
        sunriseScore,
        sunsetScore,
        alpenglowMorning,
        alpenglowEvening,
        goldenHourMorning,
        goldenHourEvening,
        hasSounding,
        soundingLevels,
        soundingTempsC,
        soundingHeightsM,
        soundingRh,
        soundingWindSpeedMph,
        soundingWindDirDeg,
      };

      this.pointCache.set(cacheKey, decoded);
      return decoded;
    } catch (err) {
      console.warn('[ForecastBinaryService] Failed to load point forecast via range query:', err);
      this.pointCache.set(cacheKey, null);
      return null;
    }
  }

  public static async getValleyFogForPoint(
    lat: number,
    lon: number,
    manifest: Manifest,
    model: string = 'nbm'
  ): Promise<DecodedValleyFog | null> {
    const zoom = manifest?.valley_fog_point_tiles?.zoom ?? 6;
    const tileX = this.lng2tile(lon, zoom);
    const tileY = this.lat2tile(lat, zoom);

    const { latMin, latMax, lonMin, lonMax } = this.tileBounds(zoom, tileX, tileY);
    if (lat < latMin || lat > latMax || lon < lonMin || lon > lonMax) {
      return null;
    }

    const gridW = manifest?.valley_fog_point_tiles?.grid_dim ?? 64;
    const gridH = manifest?.valley_fog_point_tiles?.grid_dim ?? 64;
    const px = Math.max(0, Math.min(gridW - 1, Math.floor(((lon - lonMin) / (lonMax - lonMin)) * gridW)));
    const py = Math.max(0, Math.min(gridH - 1, Math.floor(((latMax - lat) / (latMax - latMin)) * gridH)));
    const cellIdx = py * gridW + px;

    const cleanModel = (model || 'nbm').toLowerCase();
    const modelMeta = (manifest?.valley_fog_point_tiles as any)?.models_meta?.[cleanModel];
    const modelHours = modelMeta?.hours ?? (manifest?.valley_fog_point_tiles?.hours ?? 72);

    const apiServer = getApiServer(manifest);
    const tileBase = (manifest ? manifest.tile_base : 'tiles').replace(/^\/+|\/+$/g, '');
    const tileSubdir = manifest?.valley_fog_point_tiles?.tile_path || 'forecast/valley_fog_z6';
    const cleanServer = apiServer.replace(/\/$/, '');

    const hasModelSubdir = (manifest?.valley_fog_point_tiles as any)?.available_models?.includes(cleanModel);
    let subPath = hasModelSubdir ? `${tileSubdir}/${cleanModel}` : tileSubdir;
    let cleanPath = `${tileBase}/${subPath}/${tileX}/${tileY}.bin`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
    let tileUrl = `${cleanServer}/${cleanPath}`;

    const cacheKey = `${tileUrl}:${cleanModel}:${cellIdx}`;
    if (this.fogPointCache.has(cacheKey)) {
      return this.fogPointCache.get(cacheKey) || null;
    }

    const headerBytes = manifest?.valley_fog_point_tiles?.header_bytes ?? 48;
    const cellBytes = modelMeta?.cell_bytes ?? (8 + modelHours * 4);
    const startByte = headerBytes + cellIdx * cellBytes;
    const endByte = startByte + cellBytes - 1;

    try {
      let authenticatedUrl = appendAuthToken(tileUrl);
      let resp = await fetch(authenticatedUrl, {
        credentials: 'include',
        headers: {
          Range: `bytes=${startByte}-${endByte}`
        }
      });

      // Fallback to root unpartitioned directory if model subfolder 404s (do not retry on 401/402/403 denial)
      if (!resp.ok && resp.status !== 206 && resp.status !== 401 && resp.status !== 402 && resp.status !== 403 && subPath !== tileSubdir) {
        cleanPath = `${tileBase}/${tileSubdir}/${tileX}/${tileY}.bin`.replace(/\/+/g, '/');
        if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
        tileUrl = `${cleanServer}/${cleanPath}`;
        authenticatedUrl = appendAuthToken(tileUrl);
        resp = await fetch(authenticatedUrl, {
          credentials: 'include',
          headers: {
            Range: `bytes=${startByte}-${endByte}`
          }
        });
      }

      if (!resp.ok && resp.status !== 206) {
        const denial = TierAccessService.parseTileAccessDenial(resp, 'pro');
        if (denial) {
          this.lastAuthDenied = true;
          this.lastAccessDenial = denial;
          return null;
        }
        this.fogPointCache.set(cacheKey, null);
        return null;
      }
      this.lastAuthDenied = false;
      this.lastAccessDenial = null;

      let rawBuffer = await resp.arrayBuffer();

      let cellBuffer: ArrayBuffer;
      if (resp.status === 206) {
        cellBuffer = rawBuffer;
      } else if (rawBuffer.byteLength >= endByte + 1) {
        cellBuffer = rawBuffer.slice(startByte, endByte + 1);
      } else if (rawBuffer.byteLength >= 2) {
        const headerBytesArr = new Uint8Array(rawBuffer, 0, 2);
        if (headerBytesArr[0] === 0x1f && headerBytesArr[1] === 0x8b && typeof DecompressionStream !== 'undefined') {
          const stream = new Response(rawBuffer).body?.pipeThrough(new DecompressionStream('gzip'));
          if (stream) {
            const decomp = await new Response(stream).arrayBuffer();
            if (decomp.byteLength >= endByte + 1) {
              cellBuffer = decomp.slice(startByte, endByte + 1);
            } else {
              this.fogPointCache.set(cacheKey, null);
              return null;
            }
          } else {
            this.fogPointCache.set(cacheKey, null);
            return null;
          }
        } else {
          this.fogPointCache.set(cacheKey, null);
          return null;
        }
      } else {
        this.fogPointCache.set(cacheKey, null);
        return null;
      }

      if (cellBuffer.byteLength < 8) {
        this.fogPointCache.set(cacheKey, null);
        return null;
      }

      const view = new DataView(cellBuffer);
      const cycleEpoch = view.getUint32(0, true);
      const numHours = view.getUint16(4, true);

      if (numHours === 0 || cellBuffer.byteLength < 8 + numHours * 4) {
        this.fogPointCache.set(cacheKey, null);
        return null;
      }

      let byteOffset = 8;
      const fogProbArr = new Uint8Array(cellBuffer, byteOffset, numHours);
      byteOffset += numHours * 1;
      const inversionDepthArr = this.readUint16Array(view, byteOffset, numHours);
      byteOffset += numHours * 2;
      const sfcRhArr = new Uint8Array(cellBuffer, byteOffset, numHours);
      byteOffset += numHours * 1;

      const fogProbability: number[] = [];
      const inversionDepthM: number[] = [];
      const surfaceRh: number[] = [];

      for (let h = 0; h < numHours; h++) {
        fogProbability.push(fogProbArr[h]);
        inversionDepthM.push(inversionDepthArr[h]);
        surfaceRh.push(sfcRhArr[h]);
      }

      const decoded: DecodedValleyFog = {
        cycleEpoch,
        numHours,
        model,
        rawHourOffsets: getRawHourOffsetsForModel(model, numHours),
        fogProbability,
        inversionDepthM,
        surfaceRh,
      };

      this.fogPointCache.set(cacheKey, decoded);
      return decoded;
    } catch (err) {
      console.warn('[ForecastBinaryService] Failed to load valley fog point forecast:', err);
      this.fogPointCache.set(cacheKey, null);
      return null;
    }
  }

  private static readInt16Array(view: DataView, byteOffset: number, count: number): Int16Array {
    const arr = new Int16Array(count);
    for (let i = 0; i < count; i++) {
      arr[i] = view.getInt16(byteOffset + i * 2, true);
    }
    return arr;
  }

  private static readUint16Array(view: DataView, byteOffset: number, count: number): Uint16Array {
    const arr = new Uint16Array(count);
    for (let i = 0; i < count; i++) {
      arr[i] = view.getUint16(byteOffset + i * 2, true);
    }
    return arr;
  }

  private static readUint8Array(view: DataView, byteOffset: number, count: number): Uint8Array {
    return new Uint8Array(view.buffer, view.byteOffset + byteOffset, count);
  }
}
